import type { CatalogScope } from "./catalog-types";

/**
 * What of the panel fits in the URL, and how it is written back (requirement 1.7).
 *
 * Parameter names are in Portuguese because the query string is an address: it shows in
 * the browser bar and it is what gets shared. The lens and sort parameters live in
 * `entries-query.ts`, next to the parser the BFF also uses.
 */

/** Accepts both `URLSearchParams` and a route's `searchParams`. */
export type PanelQueryInput = URLSearchParams | Record<string, string | string[] | undefined>;

/**
 * Reads one parameter from either input shape.
 *
 * A repeated parameter arrives from Next as an array, and the first wins because that is
 * what `URLSearchParams.get` returns: the two shapes have to agree, or the same address
 * would mean different things on the server and on the client.
 */
export function readParam(input: PanelQueryInput, key: string): string | null {
  if (input instanceof URLSearchParams) return input.get(key);

  const value = input[key];
  if (Array.isArray(value)) return value[0] ?? null;

  return value ?? null;
}

export const CATALOG_MODE_PARAM = "modo";

/** Literal from the prototype: the catalog's add mode. */
const CATALOG_MODE_ADD = "add";

const DEFAULT_SCOPE: CatalogScope = "dentro";

/** An unknown mode falls back to the default: a stale address must not break the tab. */
export function parseCatalogScope(input: PanelQueryInput): CatalogScope {
  return readParam(input, CATALOG_MODE_PARAM) === CATALOG_MODE_ADD ? "fora" : DEFAULT_SCOPE;
}

/** Only what differs from the default is written: the open list is the bare URL. */
export function catalogScopeToSearchParams(scope: CatalogScope): URLSearchParams {
  const params = new URLSearchParams();

  if (scope !== DEFAULT_SCOPE) params.set(CATALOG_MODE_PARAM, CATALOG_MODE_ADD);

  return params;
}

/**
 * Rewrites the query string of the current address.
 *
 * Native `replaceState` rather than `router.replace`, because the filtered data is already
 * on the client and an App Router navigation would redo server work with a skeleton
 * flashing. Replaces rather than pushes so Back leaves the tab instead of unwinding every
 * refinement one at a time.
 */
export function replacePanelQuery(params: URLSearchParams): void {
  const search = params.toString();
  const { pathname, hash } = window.location;

  window.history.replaceState(null, "", `${pathname}${search.length > 0 ? `?${search}` : ""}${hash}`);
}
